import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Smartphone,
  CheckCircle2,
  Code2,
  Layers,
  Zap,
  Shield,
  RefreshCw,
  Users,
  Star,
  Globe,
  Cpu,
  Bell,
  MapPin,
  Camera,
  Lock,
  TrendingUp,
  Wrench
} from "lucide-react";

const DesarrolloMovil = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Desarrollo de Aplicaciones Móviles iOS y Android",
    "provider": {
      "@type": "Organization",
      "name": "erm-media"
    },
    "description": "Desarrollo de aplicaciones móviles nativas y multiplataforma para iOS y Android. React Native, Flutter y soluciones a medida para tu negocio.",
    "serviceType": "Desarrollo Móvil",
    "areaServed": "ES"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Desarrollo de Apps Móviles iOS y Android | erm-media"
        description="Creamos aplicaciones móviles nativas y multiplataforma para iOS y Android. Desde apps empresariales hasta soluciones de consumo, con diseño y rendimiento excepcionales."
        keywords="desarrollo móvil, aplicaciones iOS, aplicaciones Android, React Native, Flutter, app multiplataforma, desarrollo de apps, aplicaciones empresariales"
        canonicalUrl="https://erm-media.com/desarrollo-movil"
        structuredData={structuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Smartphone className="w-4 h-4" />
              <span className="text-sm font-medium">Desarrollo de Aplicaciones Móviles</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Apps para iOS y Android
              <span className="text-primary block">que conectan con tus usuarios</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Desarrollamos aplicaciones móviles de alta calidad para iPhone, iPad y dispositivos Android.
              Nativas o multiplataforma, diseñadas para ofrecer experiencias fluidas y resultados reales
              para tu negocio.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#tecnologias">
                <Button size="lg" className="group">
                  Ver Tecnologías
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="#contacto">
                <Button size="lg" variant="outline">
                  Solicitar Presupuesto
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Por qué una app móvil? */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              ¿Por qué tu negocio necesita una app móvil?
            </h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
              <p>
                Más del <strong className="text-foreground">90% del tiempo</strong> que los usuarios pasan
                en sus smartphones lo hacen dentro de aplicaciones. Una app móvil bien desarrollada
                no es un lujo, es una <strong className="text-foreground">herramienta estratégica</strong> que
                te conecta con tus clientes en el lugar donde más tiempo pasan: su teléfono.
              </p>
              <p>
                Desde aplicaciones de comercio electrónico y gestión empresarial hasta plataformas
                de fidelización y herramientas de productividad, una app te permite ofrecer
                valor a tus usuarios de forma directa, personalizada y en tiempo real.
              </p>
              <p>
                En erm-media desarrollamos aplicaciones para <strong className="text-foreground">iOS (iPhone y iPad)</strong> y
                <strong className="text-foreground"> Android</strong>, ya sea de forma nativa para el máximo
                rendimiento o multiplataforma para optimizar tiempo y presupuesto sin sacrificar calidad.
              </p>
              <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg">
                <p className="text-foreground font-semibold mb-2">
                  Dato clave:
                </p>
                <p className="text-muted-foreground">
                  Las empresas con app móvil propia aumentan su retención de clientes hasta un
                  <strong className="text-primary"> 85%</strong> y generan
                  <strong className="text-primary"> 3 veces más</strong> ingresos recurrentes que
                  las que solo tienen presencia web.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* iOS vs Android */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                iOS y Android: desarrollo para ambas plataformas
              </h2>
              <p className="text-lg text-muted-foreground">
                Conocemos a fondo los ecosistemas de Apple y Google
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* iOS */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Smartphone className="w-6 h-6 text-blue-500" />
                  </div>
                  <CardTitle className="text-2xl">iOS (Apple)</CardTitle>
                  <CardDescription>iPhone · iPad · Apple Watch</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Swift y SwiftUI:</strong> Desarrollo nativo con las últimas herramientas de Apple
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>App Store:</strong> Publicación y gestión de tu app en el ecosistema Apple
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Integración Apple Pay, Face ID, Siri</strong> y otras APIs nativas
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Seguridad y privacidad</strong> siguiendo las directrices de Apple
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Experiencia premium</strong> alineada con las guías de diseño Human Interface
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Android */}
              <Card className="border-green-500/20 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Cpu className="w-6 h-6 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl">Android (Google)</CardTitle>
                  <CardDescription>Smartphones · Tablets · Wear OS</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Kotlin y Jetpack Compose:</strong> Desarrollo nativo moderno para Android
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Google Play Store:</strong> Publicación y optimización de tu app en Play
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Integración Google Pay, Maps, Firebase</strong> y servicios de Google
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Amplia compatibilidad</strong> con la diversidad del ecosistema Android
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Material Design 3</strong> para una experiencia coherente con el ecosistema
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tecnologías multiplataforma */}
      <section id="tecnologias" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Una app, dos plataformas
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Con tecnologías multiplataforma puedes lanzar tu app en iOS y Android con un único
                equipo de desarrollo, reduciendo tiempo y costes sin comprometer la calidad
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Code2 className="w-6 h-6 text-blue-500" />
                  </div>
                  <CardTitle>React Native</CardTitle>
                  <CardDescription>JavaScript · TypeScript</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Desarrollado por Meta, permite crear apps con rendimiento casi nativo usando
                    JavaScript/TypeScript. Ideal para equipos con experiencia en React que quieren
                    llegar a iOS y Android desde una única base de código.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Layers className="w-6 h-6 text-cyan-500" />
                  </div>
                  <CardTitle>Flutter</CardTitle>
                  <CardDescription>Dart · Google</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    El framework de Google que compila a código nativo real. Ofrece un rendimiento
                    excepcional y una UI personalizable al máximo, con interfaces visualmente
                    idénticas en iOS y Android.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-purple-500" />
                  </div>
                  <CardTitle>Progressive Web Apps</CardTitle>
                  <CardDescription>Web · Instalable en móvil</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Aplicaciones web que funcionan como apps nativas: instalables en la pantalla
                    de inicio, con soporte offline y notificaciones push. La opción más económica
                    para empezar con presencia móvil.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-orange-500" />
                  </div>
                  <CardTitle>Apps Nativas iOS</CardTitle>
                  <CardDescription>Swift · SwiftUI · Xcode</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Cuando el máximo rendimiento y la integración total con el ecosistema Apple
                    son prioritarios. Acceso completo a todas las APIs de iOS, ARKit, HealthKit,
                    CoreML y más.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Cpu className="w-6 h-6 text-green-500" />
                  </div>
                  <CardTitle>Apps Nativas Android</CardTitle>
                  <CardDescription>Kotlin · Jetpack · Android Studio</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Desarrollo nativo en Kotlin con Jetpack Compose para el mercado Android.
                    Acceso completo a APIs de Google, sensores del dispositivo, personalización
                    profunda y compatibilidad con la amplia gama de dispositivos Android.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Wrench className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>Backend para Apps</CardTitle>
                  <CardDescription>APIs REST · Firebase · Laravel</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Tu app necesita un backend robusto. Desarrollamos APIs REST, integramos
                    Firebase para autenticación y base de datos en tiempo real, o construimos
                    backends personalizados en Laravel según tu proyecto.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Funcionalidades */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Funcionalidades que podemos integrar
              </h2>
              <p className="text-lg text-muted-foreground">
                Capacidades nativas del dispositivo para crear experiencias completas
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Bell className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Notificaciones Push
                  </h3>
                  <p className="text-muted-foreground">
                    Mantén a tus usuarios enganchados con notificaciones personalizadas en tiempo
                    real. Alertas de pedidos, mensajes, promociones o actualizaciones importantes
                    directamente en la pantalla de bloqueo.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Geolocalización y Mapas
                  </h3>
                  <p className="text-muted-foreground">
                    Integración con GPS para apps de delivery, logística, tiendas cercanas o
                    cualquier servicio basado en ubicación. Compatible con Google Maps y Apple Maps.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Lock className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Autenticación Biométrica
                  </h3>
                  <p className="text-muted-foreground">
                    Face ID, Touch ID y huella dactilar para un acceso seguro y rápido.
                    Tus usuarios inician sesión en segundos con la máxima seguridad sin
                    recordar contraseñas.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Camera className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Cámara, QR y Escaneo
                  </h3>
                  <p className="text-muted-foreground">
                    Acceso a la cámara para fotos, vídeos, reconocimiento de documentos y lectura
                    de códigos QR y de barras. Ideal para apps de inventario, tickets o identificación.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Sincronización Offline
                  </h3>
                  <p className="text-muted-foreground">
                    Tu app sigue funcionando sin conexión. Los datos se sincronizan
                    automáticamente cuando el usuario vuelve a estar en línea. Imprescindible
                    para apps de campo, ventas y productividad.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Pagos In-App
                  </h3>
                  <p className="text-muted-foreground">
                    Integración con Apple Pay, Google Pay, Stripe y pasarelas de pago locales.
                    Subscripciones, compras únicas y pagos recurrentes con la máxima seguridad
                    y una experiencia de checkout fluida.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso de desarrollo */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Nuestro proceso de desarrollo
              </h2>
              <p className="text-lg text-muted-foreground">
                De la idea al lanzamiento en la App Store y Google Play
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Análisis y Definición
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Estudiamos tu negocio, tus usuarios objetivo y los objetivos de la app.
                    Definimos funcionalidades, flujos de usuario y la arquitectura técnica más adecuada.
                    Esta fase nos permite darte un presupuesto exacto sin sorpresas.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Diseño UX/UI
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Creamos wireframes y prototipos interactivos antes de escribir una sola línea de código.
                    Tú apruebas el diseño completo: navegación, pantallas, colores y tipografías adaptadas
                    a las guías de diseño de iOS y Android.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Desarrollo por Sprints
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Trabajamos en ciclos ágiles de 2 semanas. Al finalizar cada sprint recibes
                    una versión funcional de la app para revisar y dar feedback. Así el desarrollo
                    avanza en la dirección correcta desde el primer día.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Pruebas y Control de Calidad
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Realizamos pruebas exhaustivas en múltiples dispositivos y versiones de sistema
                    operativo. Tests de rendimiento, seguridad, usabilidad y compatibilidad antes
                    de cualquier lanzamiento.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Publicación en Tiendas
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Gestionamos todo el proceso de publicación en la App Store de Apple y en
                    Google Play. Nos encargamos de los metadatos, capturas de pantalla optimizadas
                    y el cumplimiento de todas las políticas de las tiendas.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    6
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mantenimiento y Evolución
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    El lanzamiento es solo el comienzo. Ofrecemos planes de mantenimiento para
                    actualizar la app con nuevas versiones de iOS y Android, corregir incidencias
                    y añadir funcionalidades a medida que tu negocio crece.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de apps */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Tipos de aplicaciones que desarrollamos
              </h2>
              <p className="text-lg text-muted-foreground">
                Soluciones móviles para cada sector y necesidad
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-blue-500" />
                  </div>
                  <CardTitle>Apps Empresariales</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Herramientas de gestión interna, control de inventario, CRM móvil,
                    partes de trabajo y apps para equipos de campo que necesitan
                    trabajar conectados con la oficina.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-green-500" />
                  </div>
                  <CardTitle>E-commerce y Tiendas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Apps de compra con catálogo de productos, carrito, pagos integrados,
                    seguimiento de pedidos y programas de fidelización para aumentar
                    las ventas recurrentes.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-orange-500" />
                  </div>
                  <CardTitle>Delivery y Logística</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Plataformas de reparto, seguimiento en tiempo real de envíos,
                    asignación de rutas optimizadas y apps para repartidores y
                    clientes con geolocalización en vivo.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-purple-500" />
                  </div>
                  <CardTitle>Salud y Bienestar</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Apps de salud con integración HealthKit y Google Fit, seguimiento
                    de actividad, recordatorios de medicación, gestión de citas médicas
                    y telemedicina.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-cyan-500" />
                  </div>
                  <CardTitle>Educación y E-learning</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    Plataformas de formación online con cursos en vídeo, cuestionarios
                    interactivos, seguimiento de progreso, certificados y clases
                    en directo para alumnos y formadores.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle>A medida para tu negocio</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    ¿Tu proyecto no encaja en ninguna categoría? Perfecto. Analizamos
                    tus necesidades específicas y construimos exactamente la solución
                    que tu negocio requiere.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Estadísticas */}
      <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                El mercado móvil en cifras
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  7.3B
                </div>
                <p className="text-muted-foreground">
                  Usuarios de smartphones en el mundo en 2024
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  90%
                </div>
                <p className="text-muted-foreground">
                  Del tiempo en móvil se pasa dentro de apps, no en el navegador
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  3x
                </div>
                <p className="text-muted-foreground">
                  Mayor conversión en apps propias frente a webs móviles
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contacto" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              ¿Listo para lanzar tu app?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Cuéntanos tu idea y te asesoramos sin compromiso sobre la tecnología más
              adecuada, el alcance del proyecto y los plazos de desarrollo. Primera
              consultoría completamente gratuita.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/#contacto">
                <Button size="lg" className="group">
                  Solicitar Consultoría Gratuita
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="/soluciones-empresariales">
                <Button size="lg" variant="outline">
                  Ver Otras Soluciones
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesarrolloMovil;
